import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import ja from "./ja";
import en from "./en";
import vi from "./vi";
import id from "./id";
import zhCN from "./zh-CN";
import zhTW from "./zh-TW";
import ko from "./ko";
import th from "./th";
import tl from "./tl";
import ne from "./ne";
import pt from "./pt";
import es from "./es";
import hi from "./hi";
import my from "./my";

// 14 fully translated language dictionaries.
// `flag` field is the ISO 3166-1 alpha-2 country code consumed by https://flagcdn.com/.
export const LANGUAGES = [
  { code: "ja",    label: "日本語",            flag: "jp" },
  { code: "en",    label: "English",           flag: "us" },
  { code: "vi",    label: "Tiếng Việt",        flag: "vn" },
  { code: "id",    label: "Bahasa Indonesia",  flag: "id" },
  { code: "zh-CN", label: "简体中文",          flag: "cn" },
  { code: "zh-TW", label: "繁體中文",          flag: "tw" },
  { code: "ko",    label: "한국어",            flag: "kr" },
  { code: "th",    label: "ไทย",               flag: "th" },
  { code: "tl",    label: "Tagalog",           flag: "ph" },
  { code: "ne",    label: "नेपाली",            flag: "np" },
  { code: "pt",    label: "Português",         flag: "br" },
  { code: "es",    label: "Español",           flag: "es" },
  { code: "hi",    label: "हिन्दी",             flag: "in" },
  { code: "my",    label: "မြန်မာ",           flag: "mm" },
];

const DICTS = {
  ja, en, vi, id,
  "zh-CN": zhCN,
  "zh-TW": zhTW,
  ko, th, tl, ne, pt, es, hi, my,
};

const I18nContext = createContext({ lang: "ja", t: (k) => k, setLang: () => {} });

const STORAGE_KEY = "iqlog_lang";
const AUTO_DETECTED_KEY = "iqlog_lang_auto";

/**
 * Map a navigator.language string (e.g. "ko-KR", "pt-BR", "zh-Hant-HK")
 * to one of our 14 supported language codes. Returns null if no match.
 */
function detectBrowserLanguage() {
  if (typeof navigator === "undefined") return null;
  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean);

  for (const raw of candidates) {
    const lc = String(raw).toLowerCase();

    // Special-case Chinese variants (script + region matter)
    if (lc.startsWith("zh")) {
      if (lc.includes("hant") || /zh-(tw|hk|mo)/.test(lc)) return "zh-TW";
      return "zh-CN";
    }
    // Filipino variations
    if (lc.startsWith("fil") || lc.startsWith("tl")) return "tl";

    // Direct two-letter prefix match against supported languages
    const prefix = lc.split(/[-_]/)[0];
    const direct = LANGUAGES.find((l) => l.code === prefix);
    if (direct) return direct.code;
  }
  return null;
}

const TOAST_TEXT = {
  ja:    "言語を {label} に自動設定しました",
  en:    "Language set to {label}",
  vi:    "Đã đặt ngôn ngữ thành {label}",
  id:    "Bahasa diatur ke {label}",
  "zh-CN": "已将语言设为 {label}",
  "zh-TW": "已將語言設為 {label}",
  ko:    "언어가 {label}(으)로 설정되었습니다",
  th:    "ตั้งค่าภาษาเป็น {label} แล้ว",
  tl:    "Itinakda ang wika sa {label}",
  ne:    "भाषा {label} मा सेट गरियो",
  pt:    "Idioma definido como {label}",
  es:    "Idioma establecido en {label}",
  hi:    "भाषा {label} पर सेट की गई",
  my:    "ဘာသာစကားကို {label} ဖြင့်သတ်မှတ်ထားသည်",
};

function getPath(obj, path) {
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && DICTS[stored]) return stored;
      const detected = detectBrowserLanguage();
      if (detected && DICTS[detected]) {
        // Persist + flag this as auto-detected so we can show the toast once.
        try {
          localStorage.setItem(STORAGE_KEY, detected);
          localStorage.setItem(AUTO_DETECTED_KEY, "1");
        } catch {
          /* noop */
        }
        return detected;
      }
      return "ja";
    } catch {
      return "ja";
    }
  });

  const setLang = (code) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
      // User manually picked → clear the auto flag so toast never reappears.
      localStorage.removeItem(AUTO_DETECTED_KEY);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Show a toast on first render if the language was auto-picked from the browser.
  useEffect(() => {
    let auto;
    try {
      auto = localStorage.getItem(AUTO_DETECTED_KEY);
    } catch {
      auto = null;
    }
    if (!auto) return;

    const langMeta = LANGUAGES.find((l) => l.code === lang);
    if (!langMeta) return;
    const tmpl = TOAST_TEXT[lang] || TOAST_TEXT.en;
    const message = tmpl.replace("{label}", langMeta.label);

    // Dynamically import to avoid hard dependency at provider-evaluation time.
    import("sonner")
      .then(({ toast }) => {
        toast(message, {
          duration: 3500,
          position: "top-center",
          // Use data-testid through a custom class so QA can target it
          className: "iqlog-auto-lang-toast",
        });
      })
      .catch(() => {
        /* sonner not available — silently ignore */
      });

    try {
      localStorage.removeItem(AUTO_DETECTED_KEY);
    } catch {
      /* noop */
    }
    // intentionally only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    const dict = DICTS[lang] || DICTS.en;
    const fallback = DICTS.en;
    const t = (key, params) => {
      let out = getPath(dict, key);
      if (out === undefined) out = getPath(fallback, key);
      if (out === undefined) return key;
      if (params && typeof out === "string") {
        Object.entries(params).forEach(([k, v]) => {
          out = out.replace(new RegExp(`\\{${k}\\}`, "g"), v);
        });
      }
      return out;
    };
    // Resolve a bilingual field from API (object with ja/en keys)
    const tField = (obj) => {
      if (!obj) return "";
      if (typeof obj === "string") return obj;
      return obj[lang] || obj.en || obj.ja || Object.values(obj)[0];
    };
    return { lang, setLang, t, tField, languages: LANGUAGES };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
